import type { Node } from '@xyflow/react'

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/** A node's rect in its parent's frame: position plus measured, else width/height, else style. */
export function nodeRect(n: Node): Rect {
  return {
    x: n.position.x,
    y: n.position.y,
    width: Number(n.measured?.width ?? n.width ?? (n.style?.width as number) ?? 0),
    height: Number(n.measured?.height ?? n.height ?? (n.style?.height as number) ?? 0),
  }
}

/**
 * A frame's rect from the size it was SET to (width/height, then style), falling back to
 * measured. React Flow's measured size lags a render behind a fit, so it comes last.
 */
export function frameRect(n: Node): Rect {
  return {
    x: n.position.x,
    y: n.position.y,
    width: Number(n.width ?? n.style?.width ?? n.measured?.width ?? 0),
    height: Number(n.height ?? n.style?.height ?? n.measured?.height ?? 0),
  }
}

/** Per-sibling shifts a resize applied, keyed by node id. Only siblings that moved are listed. */
export type MadeRoom = Record<string, { dx: number; dy: number }>

/**
 * Push or pull a resized node's siblings so nothing ends up under it. A sibling
 * wholly to one side of `before` moves by that side's growth (negative on a
 * shrink); one that already overlaps `before` stays. Descendants ride along,
 * since their positions are relative. Both rects are in the node's parent
 * frame. Returns the moved nodes and the shifts applied, so the caller can
 * reverse them exactly later (see `releaseRoom`). Pure.
 */
export function makeRoomForResize<N extends Node>(
  nodes: N[],
  resizedId: string,
  before: Rect,
  after: Rect,
): { nodes: N[]; shifts: MadeRoom } {
  const shifts: MadeRoom = {}
  const resized = nodes.find((n) => n.id === resizedId)
  if (!resized) return { nodes, shifts }
  const bRight = before.x + before.width
  const bBottom = before.y + before.height
  const dRight = after.x + after.width - bRight
  const dLeft = before.x - after.x
  const dDown = after.y + after.height - bBottom
  const dUp = before.y - after.y

  const moved = nodes.map((n) => {
    if (n.id === resizedId || n.parentId !== resized.parentId) return n
    const r = nodeRect(n)
    let dx = 0
    let dy = 0
    if (r.x >= bRight) dx = dRight
    else if (r.x + r.width <= before.x) dx = -dLeft
    if (r.y >= bBottom) dy = dDown
    else if (r.y + r.height <= before.y) dy = -dUp
    if (dx === 0 && dy === 0) return n
    shifts[n.id] = { dx, dy }
    return { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
  })
  return { nodes: moved, shifts }
}

/**
 * Undo exactly what an expand pushed: move each sibling listed in the subnet's
 * `data.madeRoom` back by its recorded shift, then drop the record. A sibling
 * that is gone or no longer shares the subnet's parent is skipped. Without a
 * record nothing moves. Recomputing the pull from the frame's rects instead
 * drifts, because the frame is re-fitted after an expand.
 */
export function releaseRoom<N extends Node>(nodes: N[], subnetId: string): N[] {
  const subnet = nodes.find((n) => n.id === subnetId)
  const record = subnet?.data?.madeRoom as MadeRoom | undefined
  if (!subnet || !record) return nodes
  return nodes.map((n) => {
    if (n.id === subnetId) {
      const { madeRoom: _dropped, ...data } = n.data
      return { ...n, data }
    }
    const shift = record[n.id]
    if (!shift || n.parentId !== subnet.parentId) return n
    return { ...n, position: { x: n.position.x - shift.dx, y: n.position.y - shift.dy } }
  })
}

/** Add `shifts` to the subnet's `data.madeRoom` record, so one collapse still reverses everything. */
export function recordRoom<N extends Node>(nodes: N[], subnetId: string, shifts: MadeRoom): N[] {
  if (Object.keys(shifts).length === 0) return nodes
  return nodes.map((n) => {
    if (n.id !== subnetId) return n
    const record: MadeRoom = { ...(n.data?.madeRoom as MadeRoom | undefined) }
    for (const [id, { dx, dy }] of Object.entries(shifts)) {
      record[id] = { dx: (record[id]?.dx ?? 0) + dx, dy: (record[id]?.dy ?? 0) + dy }
    }
    return { ...n, data: { ...n.data, madeRoom: record } }
  })
}

/**
 * Where a subnet's card sat before it was expanded (`card`) and where its expanded frame
 * sat once the expand and fit finished (`frame`), both in the parent frame. Centring the
 * card on the frame cannot return to `card` after a fit moves the frame's origin, so
 * collapse restores it from this record (see `restoreCard`).
 */
export interface CardAnchor {
  card: { x: number; y: number }
  frame: { x: number; y: number }
}

/** Store the anchor on the subnet; `frame` is read from the node as it now stands. */
export function setCardAnchor<N extends Node>(
  nodes: N[],
  subnetId: string,
  card: { x: number; y: number },
): N[] {
  return nodes.map((n) =>
    n.id === subnetId
      ? { ...n, data: { ...n.data, cardAnchor: { card, frame: { ...n.position } } } }
      : n,
  )
}

/** The anchor after a fit moved the expanded frame's origin by (dx, dy) on its own. */
export function shiftedAnchor(anchor: CardAnchor, dx: number, dy: number): CardAnchor {
  return { ...anchor, frame: { x: anchor.frame.x + dx, y: anchor.frame.y + dy } }
}

/**
 * Put a just-collapsed card back where it sat before the expand, plus however far the user
 * dragged the frame since. Always drops the anchor. Without one (a subnet saved expanded)
 * the centred position stands.
 */
export function restoreCard<N extends Node>(
  nodes: N[],
  subnetId: string,
  anchor: CardAnchor | undefined,
  framePosition: { x: number; y: number },
): N[] {
  return nodes.map((n) => {
    if (n.id !== subnetId) return n
    const { cardAnchor: _dropped, ...data } = n.data
    if (!anchor) return { ...n, data }
    return {
      ...n,
      data,
      position: {
        x: anchor.card.x + framePosition.x - anchor.frame.x,
        y: anchor.card.y + framePosition.y - anchor.frame.y,
      },
    }
  })
}
